import { createBrowserRouter, Navigate } from "react-router-dom";

import { NotFoundPage, PlanListRoute, RootRoute } from "./router.js";
import { RouteErrorBoundary, RouteLoading } from "./route-boundaries.js";
import { PublicOnlyRoute, OnboardingRoute } from "../features/auth/route-guards.js";

import { RetiredPage, LegacyPlanRoute, HelpPage } from "./legacy-routes.js";

// Literal dynamic imports are intentional Vite route-level bundle boundaries.
export const router = createBrowserRouter([
  {
    path: "/terms",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { TermsPage } = await import("../features/terms/terms-page.js");
      return { Component: TermsPage };
    },
  },
  {
    path: "/welcome",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { LandingPage } = await import("../features/landing/landing-page.js");
      return { Component: LandingPage };
    },
  },
  {
    path: "/how-to/ai-agents",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { AgentPlanningGuide } = await import(
        "../features/agent-planning/agent-planning-guide.js"
      );
      return { Component: AgentPlanningGuide };
    },
  },
  {
    path: "/login",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { LoginPage } = await import("../features/auth/login-page.js");
      return {
        Component: () => (
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        ),
      };
    },
  },
  {
    path: "/signup",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { SignupPage } = await import("../features/auth/signup-page.js");
      return {
        Component: () => (
          <PublicOnlyRoute>
            <SignupPage />
          </PublicOnlyRoute>
        ),
      };
    },
  },
  {
    path: "/change-password",
    Component: () => <Navigate replace to="/login" />,
  },
  {
    path: "/auth/verify",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { VerifyMagicLinkPage } = await import("../features/auth/verify-magic-link-page.js");
      return { Component: VerifyMagicLinkPage };
    },
  },
  {
    path: "/onboarding",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { OnboardingPage } = await import("../features/auth/onboarding-page.js");
      return {
        Component: () => (
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        ),
      };
    },
  },
  {
    path: "/",
    Component: RootRoute,
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    children: [
      {
        index: true,
        Component: PlanListRoute,
      },
      {
        path: "trash",
        lazy: async () => {
          const { PlanListPage } = await import("../features/plans/plan-list-page.js");
          return { Component: PlanListPage };
        },
      },
      { path: "equipment", Component: RetiredPage },
      { path: "import", Component: () => <Navigate replace to="/" /> },
      { path: "help", Component: HelpPage },
      { path: "import/library", Component: RetiredPage },
      {
        path: "plans/:id",
        children: [
          {
            index: true,
            lazy: async () => {
              const { PlanEditorPage } = await import("../features/plans/plan-editor-page.js");
              return { Component: PlanEditorPage };
            },
          },
          { path: "viewer", Component: () => <LegacyPlanRoute stage="loading" /> },
          { path: "import", Component: () => <LegacyPlanRoute stage="cargo" importReady /> },
        ],
      },
      { path: "cargo", Component: RetiredPage },
      { path: "templates", Component: RetiredPage },
      {
        path: "settings",
        lazy: async () => {
          const { SettingsPage } = await import("../features/settings/settings-page.js");
          return { Component: SettingsPage };
        },
      },
      { path: "settings/general", Component: () => <Navigate replace to="/settings#general" /> },
      { path: "settings/members", Component: () => <Navigate replace to="/settings#members" /> },
      { path: "settings/data", Component: () => <Navigate replace to="/settings#data" /> },
    ],
  },
  {
    path: "/p/:token",
    ErrorBoundary: RouteErrorBoundary,
    HydrateFallback: RouteLoading,
    lazy: async () => {
      const { PublicSharePage } = await import("../features/share/public-share-page.js");
      return { Component: PublicSharePage };
    },
  },
  {
    path: "/foundation",
    lazy: async () => {
      const { FoundationRoute } = await import("../routes/foundation-route.js");
      return { Component: FoundationRoute };
    },
  },
  {
    path: "/status",
    lazy: async () => {
      const { StatusRoute } = await import("../routes/status-route.js");
      return { Component: StatusRoute };
    },
  },
  {
    path: "*",
    Component: NotFoundPage,
  },
]);
